import React, { useEffect, useRef, useState, useCallback } from "react";
import "./IndicatorAssistant.css";

const initialMessages = [
  { id: "sys-1", role: "system", text: "Para crear indicadores, se recomienda iniciar con la palabra 'Crear' o 'Dame'" }
];

export default function IndicatorAssistant({ onSubmit }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const toggleOpen = useCallback(() => setOpen(v => !v), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const handleSend = useCallback(async () => {
    const value = text.trim();
    if (!value || sending) return;
    const userMsg = { id: crypto.randomUUID(), role: "user", text: value };
    setMessages(prev => [...prev, userMsg]);
    setText("");
    setSending(true);

    try {
      // Espera la respuesta del backend (DirectMSG o msg)
      const replyText = (await onSubmit?.(value)) || "Sin respuesta.";
      const botMsg = { id: crypto.randomUUID(), role: "assistant", text: replyText };
      setMessages(prev => [...prev, botMsg]);
    } catch (err) {
      const botErr = {
        id: crypto.randomUUID(),
        role: "assistant",
        text: `Ocurrió un error procesando tu solicitud.`
      };
      setMessages(prev => [...prev, botErr]);
    } finally {
      setSending(false);
    }
  }, [text, onSubmit, sending]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <button className="ai-fab" aria-label="Abrir chat de indicadores" onClick={toggleOpen}>
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path d="M11 2a1 1 0 1 1 2 0v2h1a1 1 0 1 1 0 2h-1v1h3a3 3 0 0 1 3 3v6a4 4 0 0 1-4 4h-6a4 4 0 0 1-4-4V10a3 3 0 0 1 3-3h3V6h-1a1 1 0 1 1 0-2h1V2zM8 13h2v2H8v-2zm6 0h2v2h-2v-2z" />
        </svg>
      </button>

      <div className={`ai-overlay ${open ? "open" : ""}`} onClick={close} />

      <section
        className={`ai-panel ${open ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Chat de indicadores"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ai-panel__header">
          <strong>Asistente de Indicadores</strong>
          <button className="ai-icon-btn" aria-label="Cerrar" onClick={close}>✕</button>
        </header>

        <div className="ai-panel__messages">
          {messages.map(m => (
            <div key={m.id} className={`ai-msg ai-msg--${m.role}`}>
              <div className="ai-msg__bubble">{m.text}</div>
            </div>
          ))}
        </div>

        <div className="ai-panel__composer">
          <textarea
            ref={inputRef}
            className="ai-input"
            rows={2}
            placeholder='Ej: "Añade EMA 20" o "ADX 14 en panel inferior"'
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={sending}
          />
          <button className="ai-send-btn" onClick={handleSend} disabled={sending}>
            {sending ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </section>
    </>
  );
}
